import React, { useState } from 'react';
import { Car, Recycle as Motorcycle, Shield, GraduationCap, Sun, Megaphone, ChevronLeft, ChevronRight, Mail, MapPin, ChevronDown, Menu, X } from 'lucide-react';

const faqs = [
  {
    question: "Qu'est-ce que le covering automobile ?",
    answer: "Le covering automobile est une technique qui consiste à appliquer un film adhésif spécialement conçu sur la carrosserie d'un véhicule. Il permet de changer la couleur, d'ajouter des motifs personnalisés ou encore de protéger la peinture d'origine contre les rayures, petits impacts et UV."
  },
  {
    question: "Combien de temps dure un covering voiture ?",
    answer: "Un covering bien entretenu dure en moyenne entre 5 à 7 ans, selon la qualité du film et l'usage du véhicule. Chez Covering Angers, nous utilisons exclusivement des films haut de gamme garantissant une excellente durabilité."
  },
  {
    question: "Le covering endommage-t-il la peinture d'origine ?",
    answer: "Non, au contraire : le covering protège la peinture d'origine contre les agressions extérieures. Le film peut être retiré sans laisser de résidus ni endommager la carrosserie, préservant ainsi la valeur du véhicule."
  },
  {
    question: "Quel est le délai moyen pour poser un covering sur ma voiture ?",
    answer: "La durée d'application d'un covering dépend du type de véhicule et de la complexité du projet, mais en général, il faut compter entre 2 à 5 jours ouvrables. Une estimation précise est fournie lors du devis."
  },
  {
    question: "Comment entretenir un véhicule avec un covering ?",
    answer: "L'entretien d'un véhicule couvert est simple : nettoyez-le régulièrement à l'eau savonneuse douce, évitez les produits abrasifs, et n'utilisez pas de lavage haute pression trop proche du film. Nous fournissons des conseils détaillés après chaque prestation pour assurer la longévité de votre covering."
  }
];

const services = [
  {
    icon: Car,
    title: 'Covering Voiture',
    description: 'Transformation complète de votre véhicule avec des films vinyles premium. Changement de couleur, finitions mates, brillantes, chromées ou texturées. Protection garantie 5 ans et installation par nos experts certifiés.',
  },
  {
    icon: Motorcycle,
    title: 'Covering Moto',
    description: 'Solutions sur mesure pour motos avec des films spécialement conçus pour les courbes complexes. Protection optimale contre les intempéries et l\'usure, installation précise sur tous types de carénages.',
  },
  {
    icon: Shield,
    title: 'Protection PPF',
    description: 'Film de protection transparent auto-cicatrisant qui préserve la peinture des impacts, rayures et UV. Installation sur zones sensibles ou véhicule complet, garantie 10 ans sur nos films PPF.',
  },
  {
    icon: GraduationCap,
    title: 'Formation Covering',
    description: 'Programme complet de formation professionnelle : techniques de pose, préparation des surfaces, gestion de projet. Certification reconnue, supports de cours détaillés et suivi post-formation inclus.',
  },
  {
    icon: Sun,
    title: 'Vitres Teintées',
    description: "Pose de films solaires homologués réduisant jusqu'à 95% des UV et 99% des IR. Amélioration du confort thermique, protection de la vie privée et esthétique raffinée. Films conformes à la législation française."
  },
  {
    icon: Megaphone,
    title: 'Marquage Publicitaire',
    description: 'Valorisez votre image de marque avec un marquage publicitaire professionnel. Création graphique personnalisée, découpe précise et pose soignée sur tous types de véhicules. Idéal pour les flottes d\'entreprise et véhicules commerciaux.',
  },
];

const testimonials = [
  {
    name: 'Lucas D.',
    text: 'Un covering impeccable sur ma voiture, travail rapide et finition au top ! Merci Covering Angers.',
    rating: 5,
  },
  {
    name: 'Marie P.',
    text: 'Équipe professionnelle et à l\'écoute. Le résultat sur ma moto est bluffant.',
    rating: 5,
  },
  {
    name: 'Thomas B.',
    text: 'Formation covering très complète. Je recommande vivement !',
    rating: 5,
  },
];

const carouselImages = [
  'https://images.unsplash.com/photo-1603584173870-7f23fdae1b7a?auto=format&fit=crop&w=2000&q=80',
  'https://images.unsplash.com/photo-1619405399517-d7fce0f13302?auto=format&fit=crop&w=2000&q=80',
  'https://images.unsplash.com/photo-1619405399517-d7fce0f13302?auto=format&fit=crop&w=2000&q=80',
];

function App() {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [activeSection, setActiveSection] = useState('accueil');
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % carouselImages.length);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + carouselImages.length) % carouselImages.length);
  };

  React.useEffect(() => {
    const timer = setInterval(nextSlide, 5000);
    return () => clearInterval(timer);
  }, []);

  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setActiveSection(sectionId);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className="fixed top-0 w-full bg-white shadow-md z-50 transition-colors">
        <div className="container mx-auto px-4">
          <div className="flex justify-between items-center h-16">
            <div className="text-2xl font-bold text-[#E31E24]">Covering Angers</div>
            {/* Desktop Menu */}
            <div className="hidden md:flex space-x-8">
              {['accueil', 'personnalisation', 'services', 'avis', 'contact'].map((section) => (
                <button
                  key={section}
                  onClick={() => scrollToSection(section)}
                  className={`capitalize ${
                    activeSection === section ? 'text-[#E31E24]' : 'text-[#222831]'
                  } hover:text-[#00ADB5] transition-colors`}
                >
                  {section}
                </button>
              ))}
            </div>
            {/* Mobile Menu Button */}
            <button
              className="md:hidden p-2 text-[#222831] hover:text-[#E31E24] transition-colors"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            >
              {isMobileMenuOpen ? (
                <X size={24} />
              ) : (
                <Menu size={24} />
              )}
            </button>
          </div>
        </div>
        {/* Mobile Menu Panel */}
        <div
          className={`md:hidden fixed inset-0 top-16 bg-white transform transition-transform duration-300 ease-in-out ${
            isMobileMenuOpen ? 'translate-x-0' : 'translate-x-full'
          }`}
        >
          <div className="container mx-auto px-4 py-6">
            <div className="flex flex-col space-y-4">
              {['accueil', 'personnalisation', 'services', 'avis', 'contact'].map((section) => (
                <button
                  key={section}
                  onClick={() => {
                    scrollToSection(section);
                    setIsMobileMenuOpen(false);
                  }}
                  className={`text-left py-3 px-4 rounded-lg capitalize ${
                    activeSection === section
                      ? 'bg-[#E31E24] text-white'
                      : 'text-[#222831] hover:bg-gray-100'
                  } transition-colors`}
                >
                  {section}
                </button>
              ))}
            </div>
          </div>
        </div>
      </nav>

      {/* Header */}
      <section id="accueil" className="pt-16">
        <div className="relative h-[600px]">
          <div className="absolute inset-0">
            <img
              src={carouselImages[currentSlide]}
              alt="Covering"
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-black bg-opacity-50" />
          </div>
          <div className="relative container mx-auto px-4 h-full flex flex-col justify-center items-center text-white text-center">
            <h1 className="text-5xl font-bold mb-4">Covering Angers</h1>
            <p className="text-xl mb-8">
              Personnalisez, protégez et sublimez votre véhicule avec le covering automobile à Angers.
            </p>
            <button
              onClick={() => scrollToSection('contact')}
              className="bg-[#E31E24] hover:bg-[#00ADB5] text-white px-8 py-3 rounded-full transition-colors"
            >
              Demander un devis gratuit
            </button>
          </div>
          <button
            onClick={prevSlide}
            className="absolute left-4 top-1/2 -translate-y-1/2 bg-white/20 p-2 rounded-full hover:bg-white/40 transition-colors"
          >
            <ChevronLeft className="text-white" size={24} />
          </button>
          <button
            onClick={nextSlide}
            className="absolute right-4 top-1/2 -translate-y-1/2 bg-white/20 p-2 rounded-full hover:bg-white/40 transition-colors"
          >
            <ChevronRight className="text-white" size={24} />
          </button>
        </div>
      </section>

      {/* Personnalisation */}
      <section id="personnalisation" className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <h2 className="text-4xl font-bold text-center text-[#222831] mb-8">
            Personnalisez votre véhicule selon vos envies !
          </h2>
          <div className="flex flex-col md:flex-row items-center gap-12">
            <div className="md:w-1/2">
              <div className="space-y-4 text-lg text-[#393E46] leading-relaxed">
                <p>
                  Bienvenue chez <strong>Covering Angers</strong>, votre <strong>expert en personnalisation automobile</strong> depuis plus de 10 ans.
                </p>
                <p>
                  Notre équipe de <strong>professionnels passionnés</strong> met son savoir-faire à votre service pour <strong>transformer votre véhicule</strong> selon vos désirs. Nous utilisons exclusivement des <strong>matériaux haut de gamme</strong> et les dernières techniques de pose pour garantir un résultat exceptionnel et durable.
                </p>
                <p>
                  Que ce soit pour un changement de couleur complet, une protection invisible ou un design unique, nous vous accompagnons dans la réalisation de votre projet avec conseil et expertise.
                </p>
                <p>
                  Notre engagement : faire de votre véhicule un <strong>modèle unique</strong> qui vous ressemble, tout en préservant sa valeur.
                </p>
              </div>
            </div>
            <div className="md:w-1/2">
              <img
                src="https://images.unsplash.com/photo-1494905998402-395d579af36f?auto=format&fit=crop&w=1000&q=80"
                alt="Personnalisation"
                className="rounded-lg shadow-xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Services */}
      <section id="services" className="py-16">
        <div className="container mx-auto px-4">
          <h2 className="text-4xl font-bold text-center text-[#222831] mb-12">
            Découvrez nos services de covering à Angers
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <div
                key={index}
                className="bg-white rounded-xl shadow-lg p-6 hover:shadow-xl transition-shadow"
              >
                <service.icon className="w-12 h-12 text-[#E31E24] mb-4" />
                <h3 className="text-2xl font-bold text-[#222831] mb-4">{service.title}</h3>
                <p className="text-[#393E46]">{service.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section id="avis" className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <h2 className="text-4xl font-bold text-center text-[#222831] mb-12">
            Nos clients témoignent
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <div key={index} className="bg-white rounded-xl shadow-lg p-6">
                <div className="flex items-center mb-4">
                  <div className="text-[#E31E24]">
                    {'★'.repeat(testimonial.rating)}
                    {'☆'.repeat(5 - testimonial.rating)}
                  </div>
                </div>
                <p className="text-[#393E46] mb-4 italic">"{testimonial.text}"</p>
                <p className="font-medium text-[#222831]">– {testimonial.name}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact */}
      <section id="contact" className="py-16">
        <div className="container mx-auto px-4">
          <h2 className="text-4xl font-bold text-center text-[#222831] mb-4">Contactez-nous</h2>
          <p className="text-center text-[#393E46] mb-12">
            Demandez un devis gratuit ou posez vos questions en remplissant ce formulaire. Réponse
            rapide assurée !
          </p>
          <div className="max-w-2xl mx-auto">
            <form className="space-y-6">
              <div>
                <input
                  type="text"
                  placeholder="Nom"
                  className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-[#00ADB5] focus:ring-1 focus:ring-[#00ADB5] outline-none transition-colors"
                />
              </div>
              <div>
                <input
                  type="email"
                  placeholder="Email"
                  className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-[#00ADB5] focus:ring-1 focus:ring-[#00ADB5] outline-none transition-colors"
                />
              </div>
              <div>
                <input
                  type="tel"
                  placeholder="Téléphone"
                  className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-[#00ADB5] focus:ring-1 focus:ring-[#00ADB5] outline-none transition-colors"
                />
              </div>
              <div>
                <select className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-[#00ADB5] focus:ring-1 focus:ring-[#00ADB5] outline-none transition-colors">
                  <option value="">Service souhaité</option>
                  <option value="covering-voiture">Covering voiture</option>
                  <option value="covering-moto">Covering moto</option>
                  <option value="ppf">Protection PPF</option>
                  <option value="formation">Formation</option>
                  <option value="vitres-teintees">Vitres teintées</option>
                  <option value="marquage-publicitaire">Marquage publicitaire</option>
                </select>
              </div>
              <div>
                <textarea
                  placeholder="Message"
                  rows={4}
                  className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-[#00ADB5] focus:ring-1 focus:ring-[#00ADB5] outline-none transition-colors"
                ></textarea>
              </div>
              <button
                type="submit"
                className="w-full bg-[#E31E24] hover:bg-[#00ADB5] text-white font-medium py-3 rounded-lg transition-colors"
              >
                Envoyer ma demande
              </button>
            </form>
            <div className="mt-12 flex flex-col md:flex-row justify-center items-center gap-8 text-[#393E46]">
              <div className="flex items-center gap-2">
                <MapPin className="text-[#E31E24]" />
                <span>Angers, 49000</span>
              </div>
              <div className="flex items-center gap-2">
                <Mail className="text-[#E31E24]" />
                <a href="mailto:info@coveringangers.fr" className="hover:text-[#00ADB5]">
                  info@coveringangers.fr
                </a>
              </div>
            </div>
          </div>
          
          {/* FAQ Section */}
          <div className="mt-24 p-12 bg-gray-50 rounded-2xl">
            <h3 className="text-3xl font-bold text-center text-[#222831] mb-8">
              Questions fréquentes sur le covering voiture
            </h3>
            <div className="space-y-4 max-w-3xl mx-auto">
              {faqs.map((faq, index) => (
                <div
                  key={index}
                  className="bg-white rounded-lg shadow-lg overflow-hidden hover:shadow-xl transition-shadow"
                >
                  <button
                    className="w-full px-6 py-4 text-left flex justify-between items-center hover:bg-gray-50 transition-colors"
                    onClick={() => setOpenFaq(openFaq === index ? null : index)}
                  >
                    <span className="font-medium text-[#222831]">{faq.question}</span>
                    <ChevronDown
                      className={`text-[#E31E24] transition-transform ${
                        openFaq === index ? 'rotate-180' : ''
                      }`}
                      size={20}
                    />
                  </button>
                  <div
                    className={`px-6 transition-all duration-200 ease-in-out ${
                      openFaq === index ? 'py-4' : 'py-0 h-0'
                    }`}
                  >
                    <p className="text-[#393E46]">{faq.answer}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Partners */}
      <div className="pt-4 pb-8">
        <div className="container mx-auto px-4">
          <h3 className="text-2xl font-bold text-center text-[#222831] mb-4">
            Nos partenaires de confiance
          </h3>
          <div className="flex flex-wrap justify-center items-center gap-8 md:gap-16">
            <a href="https://www.3mfrance.fr/3M/fr_FR/automobile-france/" target="_blank" rel="noopener noreferrer">
              <img 
                src="https://www.coveringangers.fr/3M.JPG"
                alt="3M"
                className="h-12 grayscale hover:grayscale-0 transition-all duration-300"
              />
            </a>
            <a href="https://www.orafol.com/fr/" target="_blank" rel="noopener noreferrer">
              <img 
                src="https://www.coveringangers.fr/ORAFOL.JPG"
                alt="ORAFOL"
                className="h-12 grayscale hover:grayscale-0 transition-all duration-300"
              />
            </a>
            <a href="https://www.kpmf-films.com/fr/" target="_blank" rel="noopener noreferrer">
              <img 
                src="https://www.coveringangers.fr/KPMF.JPG"
                alt="KPMF"
                className="h-12 grayscale hover:grayscale-0 transition-all duration-300"
              />
            </a>
            <a href="https://www.covering.fr" target="_blank" rel="noopener noreferrer" className="order-first">
              <img 
                src="https://www.coveringangers.fr/COVERINGFR.JPG"
                alt="covering.fr"
                className="h-14 grayscale hover:grayscale-0 transition-all duration-300"
              />
            </a>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-[#222831] text-white py-8">
        <div className="container mx-auto px-4">
          <div className="text-center">
            <div className="text-2xl font-bold mb-4">Covering Angers</div>
            <p className="text-sm text-gray-400">
              © 2025 Covering Angers. Tous droits réservés.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default App;